// src/pages/AllianceManagement/PlayerManagementTab.js
import { ErrorContext } from '../../context/ErrorContext';
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import NationSelector from '../../components/NationSelector';
import MultiSelectFilter from '../../components/MultiSelectFilter';
import HasPermission from '../../utils/HasPermission';
import BattleReportModal from '../../components/events/BattleReportModal';
import './css/PlayerManagementTab.css';
import { AppButton } from '../../components/ui';

const apiUrl = API_URL;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const toText = (value) => String(value || '').trim().toLowerCase();

const DEFAULT_MANUAL_STRIKE_FORM = {
    warningLevelDelta: '1',
    expiryDays: '14',
    reasonNote: '',
};

const DEFAULT_NEW_EVIDENCE_FORM = {
    reasonModel: 'EventBattle',
    reasonRef: '',
    label: '',
    summary: '',
};

const STRIKE_REPORT_SEGMENT_LIMIT = 480;

const truncateReportLine = (line) => {
    const normalized = String(line || '').replace(/\s+/g, ' ').trim();
    if (normalized.length <= STRIKE_REPORT_SEGMENT_LIMIT) return normalized;
    return `${normalized.slice(0, STRIKE_REPORT_SEGMENT_LIMIT - 3).trim()}...`;
};

const splitReportLinesIntoSegments = (lines) => {
    const segments = [];
    let current = '';
    lines.forEach((rawLine) => {
        const line = truncateReportLine(rawLine);
        if (!line) return;
        const next = current ? `${current}\n${line}` : line;
        if (next.length > STRIKE_REPORT_SEGMENT_LIMIT && current) {
            segments.push(current);
            current = line;
        } else {
            current = next;
        }
    });
    if (current) segments.push(current);
    return segments;
};

const getStrikeReasonText = (strike) => {
    const label = String(strike?.summaryLabel || strike?.reasonLabel || strike?.reason?.label || '').trim();
    const detail = String(strike?.summaryDetail || strike?.reasonNote || '').trim();
    if (label && detail && !detail.toLowerCase().includes(label.toLowerCase())) return `${label}: ${detail}`;
    return detail || label || 'active strike';
};

const toDateTimeInputValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fromDateTimeInputValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const normalizeStrikeEvidenceRefs = (strike) => {
    const rawRefs = Array.isArray(strike?.evidenceRefs) ? strike.evidenceRefs : [];
    if (rawRefs.length > 0) {
        return rawRefs
            .map((ref) => ({
                reasonModel: String(ref.reasonModel || '').trim(),
                reasonRef: String(ref.reasonRef || ref.reason?.id || '').trim(),
                label: String(ref.label || '').trim(),
                summary: String(ref.summary || '').trim(),
                battleTime: ref.battleTime || null,
            }))
            .filter((ref) => ref.reasonModel && ref.reasonRef);
    }
    if (strike?.reasonModel && (strike?.reasonRef || strike?.reason?.id)) {
        return [{
            reasonModel: String(strike.reasonModel || '').trim(),
            reasonRef: String(strike.reasonRef || strike.reason?.id || '').trim(),
            label: String(strike.reason?.label || strike.reason?.reportKey || strike.reason?.battleKey || '').trim(),
            summary: '',
            battleTime: strike.reason?.battleTime || null,
        }];
    }
    return [];
};

const rankSortValue = (rank) => {
    const normalized = String(rank || '').trim().toLowerCase();
    const rankOrder = {
        r5: 5,
        r4: 4,
        r3: 3,
        r2: 2,
        r1: 1,
        leader: 5,
        officer: 4,
        member: 1,
    };
    if (rankOrder[normalized] != null) return rankOrder[normalized];
    const parsed = Number(String(rank || '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const PLAYER_TABLE_COLUMNS = [
    {
        key: 'name',
        label: 'Name',
        type: 'text',
        getValue: (commander) => commander.name,
    },
    {
        key: 'alliance',
        label: 'Alliance',
        type: 'text',
        getValue: (commander) => commander.alliance?.acronym || commander.alliance?.name,
    },
    {
        key: 'rank',
        label: 'Rank',
        type: 'number',
        defaultDirection: 'desc',
        getValue: (commander) => rankSortValue(commander.rank),
    },
    {
        key: 'battlePower',
        label: 'Battle Power',
        type: 'number',
        defaultDirection: 'desc',
        getValue: (commander) => commander.battlePower,
    },
    {
        key: 'warningLevel',
        label: 'Warning Level',
        type: 'number',
        defaultDirection: 'desc',
        getValue: (commander) => commander.warningLevel,
    },
    {
        key: 'totalStrikes',
        label: 'Total Strikes',
        type: 'number',
        defaultDirection: 'desc',
        getValue: (commander) => commander.totalStrikes,
    },
    {
        key: 'location',
        label: 'Expected City Location',
        type: 'text',
        getValue: (commander) => commander.location,
    },
];

const PlayerManagementTab = () => {
    const { user, isAuthInitializing } = useContext(AuthContext);
    const { showError } = useContext(ErrorContext);
    const canWriteCommanders = HasPermission('writeCommanders');
    const normalizedUserRole = String(user?.role || '').trim().toLowerCase().replace(/[^a-z]/g, '');
    const canManageStrikes = canWriteCommanders || normalizedUserRole === 'superuser' || normalizedUserRole === 'supporter';
    const canEditStrikes = normalizedUserRole === 'superuser';

    // States for nation and alliance filtering
    const [nations, setNations] = useState([]);
    const [selectedNation, setSelectedNation] = useState(null);
    const [alliances, setAlliances] = useState([]);
    const [selectedAllianceIds, setSelectedAllianceIds] = useState([]);
    const [selectedCommanderIds, setSelectedCommanderIds] = useState([]);

    // State for fetched commanders data and loading flag
    const [commanders, setCommanders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [strikeModalCommander, setStrikeModalCommander] = useState(null);
    const [strikeHistory, setStrikeHistory] = useState([]);
    const [isStrikeHistoryLoading, setIsStrikeHistoryLoading] = useState(false);
    const [strikeHistoryError, setStrikeHistoryError] = useState('');
    const [manualStrikeCommander, setManualStrikeCommander] = useState(null);
    const [manualStrikeForm, setManualStrikeForm] = useState(DEFAULT_MANUAL_STRIKE_FORM);
    const [isSavingManualStrike, setIsSavingManualStrike] = useState(false);
    const [isDeletingStrikeId, setIsDeletingStrikeId] = useState('');
    const [editingStrike, setEditingStrike] = useState(null);
    const [editStrikeForm, setEditStrikeForm] = useState({ expiresAt: '', reasonNote: '' });
    const [editEvidenceRefs, setEditEvidenceRefs] = useState([]);
    const [newEvidenceForm, setNewEvidenceForm] = useState(DEFAULT_NEW_EVIDENCE_FORM);
    const [isSavingEditStrike, setIsSavingEditStrike] = useState(false);
    const [isExpiringStrike, setIsExpiringStrike] = useState(false);
    const [selectedStrikeReason, setSelectedStrikeReason] = useState(null);
    const [selectedEvidenceStrike, setSelectedEvidenceStrike] = useState(null);
    const [isStrikeReportOpen, setIsStrikeReportOpen] = useState(false);
    const [isStrikeReportLoading, setIsStrikeReportLoading] = useState(false);
    const [strikeReportError, setStrikeReportError] = useState('');
    const [strikeReportSegments, setStrikeReportSegments] = useState([]);
    const [copiedStrikeReportIndex, setCopiedStrikeReportIndex] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'warningLevel', direction: 'desc' });

    const columnsByKey = useMemo(
        () => new Map(PLAYER_TABLE_COLUMNS.map((column) => [column.key, column])),
        []
    );

    const sortedCommanders = useMemo(() => {
        const column = columnsByKey.get(sortConfig.key) || PLAYER_TABLE_COLUMNS[0];
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        const selectedCommanderSet = new Set(selectedCommanderIds.map((value) => String(value)));
        const filteredCommanders = selectedCommanderSet.size > 0
            ? commanders.filter((commander) => selectedCommanderSet.has(String(commander._id)))
            : commanders;
        return [...filteredCommanders].sort((left, right) => {
            const leftValue = column.getValue(left);
            const rightValue = column.getValue(right);
            let result;
            if (column.type === 'number') {
                result = toNumber(leftValue) - toNumber(rightValue);
            } else {
                result = toText(leftValue).localeCompare(toText(rightValue), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            }
            if (result === 0) {
                result = toText(left.name).localeCompare(toText(right.name), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            }
            return result * direction;
        });
    }, [columnsByKey, commanders, selectedCommanderIds, sortConfig]);

    const allianceOptions = useMemo(() => {
        return [...alliances]
            .sort((left, right) => {
                const battlePowerDelta = toNumber(right.battlePower) - toNumber(left.battlePower);
                if (battlePowerDelta !== 0) return battlePowerDelta;
                return toText(left.acronym || left.name).localeCompare(toText(right.acronym || right.name), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            })
            .map((alliance) => {
                const acronym = String(alliance.acronym || '').trim();
                const name = String(alliance.name || '').trim();
                const labelName = acronym && name ? `${acronym} - ${name}` : (acronym || name || 'Unknown alliance');
                return {
                    value: alliance._id,
                    label: `${labelName} (${toNumber(alliance.battlePower).toLocaleString()} BP)`,
                };
            });
    }, [alliances]);

    const commanderOptions = useMemo(() => {
        return [...commanders]
            .sort((left, right) => {
                const battlePowerDelta = toNumber(right.battlePower) - toNumber(left.battlePower);
                if (battlePowerDelta !== 0) return battlePowerDelta;
                return toText(left.name).localeCompare(toText(right.name), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            })
            .map((commander) => {
                const allianceLabel = commander.alliance?.acronym || commander.alliance?.name || 'N/A';
                const battlePower = toNumber(commander.battlePower).toLocaleString();
                return {
                    value: commander._id,
                    label: `${commander.name || 'N/A'} (${allianceLabel}, ${battlePower} BP)`,
                };
            });
    }, [commanders]);

    const buildStrikeReportSegments = useCallback((strikes) => {
        const commanderById = new Map(sortedCommanders.map((commander) => [String(commander._id), commander]));
        const grouped = new Map();
        (Array.isArray(strikes) ? strikes : []).forEach((strike) => {
            const commanderId = String(strike?.commander?._id || strike?.commander || '').trim();
            if (!commanderId || !commanderById.has(commanderId)) return;
            const status = String(strike?.effectiveStatus || strike?.status || 'active').toLowerCase();
            if (status !== 'active') return;
            if (!grouped.has(commanderId)) {
                grouped.set(commanderId, {
                    commander: commanderById.get(commanderId),
                    reasons: [],
                });
            }
            grouped.get(commanderId).reasons.push(getStrikeReasonText(strike));
        });

        const lines = Array.from(grouped.values())
            .sort((left, right) => {
                const warningDelta = toNumber(right.commander.warningLevel) - toNumber(left.commander.warningLevel);
                if (warningDelta !== 0) return warningDelta;
                return toText(left.commander.name).localeCompare(toText(right.commander.name), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            })
            .map(({ commander, reasons }) => {
                const level = Math.max(0, Math.trunc(toNumber(commander.warningLevel)));
                const uniqueReasons = Array.from(new Set(reasons.map((reason) => String(reason || '').trim()).filter(Boolean)));
                return `- ${commander.name || 'N/A'}: level ${level}, ${uniqueReasons.join('; ') || 'active strike'}`;
            });

        return splitReportLinesIntoSegments(lines);
    }, [sortedCommanders]);

    const setAllianceFilter = useCallback((updater) => {
        setSelectedCommanderIds([]);
        setSelectedAllianceIds(updater);
    }, []);

    const toggleSort = (key) => {
        const column = columnsByKey.get(key) || PLAYER_TABLE_COLUMNS[0];
        setSortConfig((current) => {
            if (current.key === key) {
                return {
                    key,
                    direction: current.direction === 'asc' ? 'desc' : 'asc',
                };
            }
            return {
                key,
                direction: column.defaultDirection || 'asc',
            };
        });
    };

    const renderSortableHeader = (column) => {
        const isActive = sortConfig.key === column.key;
        const indicator = isActive ? (sortConfig.direction === 'asc' ? '^' : 'v') : '-';
        return (
            <button
                type="button"
                className={`am-sort-button ${isActive ? 'active' : ''}`}
                onClick={() => toggleSort(column.key)}
            >
                <span>{column.label}</span>
                <span className="am-sort-indicator">{indicator}</span>
            </button>
        );
    };

    const formatDateTime = (value) => {
        if (!value) return 'N/A';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleString();
    };

    const formatReasonType = (value) => {
        const normalized = String(value || '').trim();
        if (normalized === 'EventBattle') return 'Battle';
        if (normalized === 'AoZBattleReport') return 'Battle report';
        if (normalized === 'EventResultEntry') return 'Event result';
        return normalized || 'Manual';
    };

    const selectNation = useCallback((nation) => {
        setSelectedAllianceIds([]);
        setSelectedCommanderIds([]);
        setAlliances([]);
        setCommanders([]);
        setSelectedNation(nation);
    }, []);

    const updateCommanderInState = useCallback((updatedCommander) => {
        if (!updatedCommander?._id) return;
        setCommanders((prev) => prev.map((commander) => (
            commander._id === updatedCommander._id
                ? { ...commander, ...updatedCommander }
                : commander
        )));
        setStrikeModalCommander((prev) => (
            prev?._id === updatedCommander._id
                ? { ...prev, ...updatedCommander }
                : prev
        ));
        setManualStrikeCommander((prev) => (
            prev?._id === updatedCommander._id
                ? { ...prev, ...updatedCommander }
                : prev
        ));
    }, []);

    const openStrikeHistory = async (commander) => {
        if (!commander?._id) return;
        setStrikeModalCommander(commander);
        setStrikeHistory([]);
        setStrikeHistoryError('');
        setIsStrikeHistoryLoading(true);
        try {
            const res = await axiosInstance.get(`${apiUrl}/commander-strikes`, {
                params: {
                    commanderId: commander._id,
                    limit: 200,
                },
            });
            setStrikeHistory(Array.isArray(res.data?.strikes) ? res.data.strikes : []);
        } catch (error) {
            console.error('Error fetching commander strike history:', error);
            setStrikeHistoryError(error?.response?.data?.message || 'Failed to fetch strike history.');
        } finally {
            setIsStrikeHistoryLoading(false);
        }
    };

    const openManualStrikeModal = (commander) => {
        if (!commander?._id) return;
        setManualStrikeCommander(commander);
        setManualStrikeForm(DEFAULT_MANUAL_STRIKE_FORM);
    };

    const openEditStrikeModal = (strike) => {
        if (!strike?._id || !canEditStrikes) return;
        setEditingStrike(strike);
        setEditStrikeForm({
            expiresAt: toDateTimeInputValue(strike.expiresAt),
            reasonNote: strike.reasonNote || '',
        });
        setEditEvidenceRefs(normalizeStrikeEvidenceRefs(strike));
        setNewEvidenceForm(DEFAULT_NEW_EVIDENCE_FORM);
    };

    const removeEditEvidenceRef = (indexToRemove) => {
        setEditEvidenceRefs((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const addEditEvidenceRef = () => {
        const reasonModel = String(newEvidenceForm.reasonModel || '').trim();
        const reasonRef = String(newEvidenceForm.reasonRef || '').trim();
        if (!reasonModel || !reasonRef) {
            window.alert('Select an evidence type and provide the report or battle id.');
            return;
        }
        const key = `${reasonModel}:${reasonRef}`;
        if (editEvidenceRefs.some((ref) => `${ref.reasonModel}:${ref.reasonRef}` === key)) {
            window.alert('That evidence is already attached.');
            return;
        }
        setEditEvidenceRefs((prev) => [
            ...prev,
            {
                reasonModel,
                reasonRef,
                label: String(newEvidenceForm.label || '').trim(),
                summary: String(newEvidenceForm.summary || '').trim(),
                battleTime: null,
            },
        ]);
        setNewEvidenceForm(DEFAULT_NEW_EVIDENCE_FORM);
    };

    const submitEditStrike = async ({ expireImmediately = false } = {}) => {
        if (!editingStrike?._id || isSavingEditStrike || isExpiringStrike) return;
        const reasonNote = String(editStrikeForm.reasonNote || '').trim();
        if (!reasonNote) {
            window.alert('A reason note is required.');
            return;
        }
        const expiresAt = fromDateTimeInputValue(editStrikeForm.expiresAt);
        if (!expireImmediately && !expiresAt) {
            window.alert('A valid expiry date is required.');
            return;
        }

        if (expireImmediately) {
            setIsExpiringStrike(true);
        } else {
            setIsSavingEditStrike(true);
        }
        try {
            const response = await axiosInstance.patch(`${apiUrl}/commander-strikes/${editingStrike._id}`, {
                reasonNote,
                evidenceRefs: editEvidenceRefs,
                ...(expireImmediately ? { expireImmediately: true } : { expiresAt }),
            });
            if (response.data?.strike) {
                setStrikeHistory((prev) => prev.map((row) => (
                    row._id === response.data.strike._id ? response.data.strike : row
                )));
            }
            updateCommanderInState(response.data?.commander);
            setEditingStrike(null);
        } catch (error) {
            console.error('Error updating strike:', error);
            window.alert(error?.response?.data?.message || 'Failed to update strike.');
        } finally {
            setIsSavingEditStrike(false);
            setIsExpiringStrike(false);
        }
    };

    const submitManualStrike = async () => {
        if (!manualStrikeCommander?._id || isSavingManualStrike) return;
        const warningLevelDelta = Math.trunc(Number(manualStrikeForm.warningLevelDelta || 1));
        if (!Number.isInteger(warningLevelDelta) || warningLevelDelta < 1 || warningLevelDelta > 10) {
            window.alert('Strike delta must be a whole number from 1 to 10.');
            return;
        }
        const expiryDays = Math.trunc(Number(manualStrikeForm.expiryDays || 14));
        if (!Number.isInteger(expiryDays) || expiryDays < 1 || expiryDays > 3650) {
            window.alert('Expiry days must be a whole number from 1 to 3650.');
            return;
        }
        const reasonNote = String(manualStrikeForm.reasonNote || '').trim();
        if (!reasonNote) {
            window.alert('A reason note is required.');
            return;
        }

        setIsSavingManualStrike(true);
        try {
            const response = await axiosInstance.post(`${apiUrl}/commander-strikes`, {
                commanderId: manualStrikeCommander._id,
                warningLevelDelta,
                expiryDays,
                reasonCode: 'manual_player_management',
                reasonLabel: 'Manual player management',
                reasonNote,
            });
            updateCommanderInState(response.data?.commander);
            if (strikeModalCommander?._id === manualStrikeCommander._id && response.data?.strike) {
                setStrikeHistory((prev) => [response.data.strike, ...prev]);
            }
            setManualStrikeCommander(null);
            setManualStrikeForm(DEFAULT_MANUAL_STRIKE_FORM);
        } catch (error) {
            console.error('Error creating manual strike:', error);
            window.alert(error?.response?.data?.message || 'Failed to create strike.');
        } finally {
            setIsSavingManualStrike(false);
        }
    };

    const deleteStrike = async (strike) => {
        if (!strike?._id || isDeletingStrikeId) return;
        const confirmed = window.confirm('Delete this strike? The commander warning level will be recalculated from active strikes.');
        if (!confirmed) return;

        setIsDeletingStrikeId(strike._id);
        try {
            const response = await axiosInstance.delete(`${apiUrl}/commander-strikes/${strike._id}`);
            setStrikeHistory((prev) => prev.filter((row) => row._id !== strike._id));
            updateCommanderInState(response.data?.commander);
        } catch (error) {
            console.error('Error deleting strike:', error);
            window.alert(error?.response?.data?.message || 'Failed to delete strike.');
        } finally {
            setIsDeletingStrikeId('');
        }
    };

    const fetchAllNations = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`${apiUrl}/nations`);
            const rows = Array.isArray(res.data) ? res.data : [];
            const userNationId = typeof user?.nation === 'string' ? user.nation : user?.nation?._id;
            setNations(rows);
            if (rows.length > 0) {
                selectNation(rows.find((nation) => nation._id === userNationId) || rows[0]);
            }
        } catch (error) {
            console.error('Error fetching nations:', error);
        }
    }, [selectNation, user?.nation]);

    const fetchNationById = useCallback(async (nationId) => {
        try {
            const res = await axiosInstance.get(`${apiUrl}/nations/${nationId}`);
            selectNation(res.data);
        } catch (error) {
            console.error('Error fetching nation by ID:', error);
        }
    }, [selectNation]);

    // On mount: For superUser, fetch all nations; for nationAdmin, set the selectedNation to user's nation.
    useEffect(() => {
        if (!user) return;
        if (user.role === 'superUser') {
            fetchAllNations();
        } else if (user.role === 'nationAdmin' || user.role === 'supporter') {
            // If user.nation is a string, fetch the full nation document; otherwise, set it directly.
            if (typeof user.nation === 'string') {
                fetchNationById(user.nation);
            } else if (user.nation && user.nation._id) {
                selectNation(user.nation);
            }
        }
    }, [fetchAllNations, fetchNationById, selectNation, user]);

    // Function to handle copying the location info to the clipboard
    const copyToClipboard = (commander) => {
        const acronym = commander.alliance?.acronym ? commander.alliance.acronym : `N/A`
        const location = commander.location ? commander.location : `UNKOWN`
        const locationString = `(${acronym}) ${commander.name}, Expected location: ${location}`;
        navigator.clipboard.writeText(locationString)
            .then(() => {
                showError('Location info copied to clipboard!');
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    const copyStrikeReportSegment = async (segment, index) => {
        try {
            await navigator.clipboard.writeText(segment);
            setCopiedStrikeReportIndex(index);
            showError('Strike report block copied to clipboard.');
            window.setTimeout(() => {
                setCopiedStrikeReportIndex((current) => (current === index ? null : current));
            }, 1800);
        } catch (error) {
            console.error('Failed to copy strike report block:', error);
            window.alert('Failed to copy strike report block.');
        }
    };

    const openStrikeReport = async () => {
        if (!selectedNation?._id || isStrikeReportLoading) return;
        setIsStrikeReportOpen(true);
        setIsStrikeReportLoading(true);
        setStrikeReportError('');
        setStrikeReportSegments([]);
        setCopiedStrikeReportIndex(null);

        try {
            const params = {
                nationId: selectedNation._id,
                status: 'active',
                limit: 5000,
            };
            if (selectedCommanderIds.length > 0) {
                params.commanderIds = selectedCommanderIds.join(',');
            }
            const res = await axiosInstance.get(`${apiUrl}/commander-strikes`, { params });
            const segments = buildStrikeReportSegments(Array.isArray(res.data?.strikes) ? res.data.strikes : []);
            setStrikeReportSegments(segments);
        } catch (error) {
            console.error('Error building strike report:', error);
            setStrikeReportError(error?.response?.data?.message || 'Failed to build strike report.');
        } finally {
            setIsStrikeReportLoading(false);
        }
    };

    // When selectedNation changes, fetch alliances for that nation.
    useEffect(() => {
        if (selectedNation && selectedNation._id) {
            fetchAlliances(selectedNation._id);
        }
    }, [selectedNation]);

    const fetchAlliances = async (nationId) => {
        try {
            const res = await axiosInstance.get(`${apiUrl}/nations/${nationId}/alliances`);
            setAlliances(res.data);
        } catch (error) {
            console.error('Error fetching alliances:', error);
        }
    };

    const fetchCommanders = useCallback(async (nationId, allianceIds = []) => {
        setIsLoading(true);
        try {
            const params = {
                limit: 10000,
            };
            if (allianceIds.length > 0) {
                params.allianceIds = allianceIds.join(',');
            }
            const res = await axiosInstance.get(`${apiUrl}/nations/${nationId}/commanders`, { params });
            setCommanders(res.data.commanders || []);
        } catch (error) {
            console.error('Error fetching commanders for nation:', error);
            setCommanders([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedNation?._id) {
            fetchCommanders(selectedNation._id, selectedAllianceIds);
        } else {
            setCommanders([]);
        }
    }, [fetchCommanders, selectedAllianceIds, selectedNation?._id]);

    if (!user) {
        return <p>{isAuthInitializing ? 'Loading player management...' : 'Please sign in to manage players.'}</p>;
    }

    if (user.role === 'user') {
        return <p>You do not have permission to manage players.</p>;
    }

    return (
        <div className="am-player-tab">
            <h2>Player Management</h2>
            {/* For superUser, show NationSelector; for nationAdmin, the nation is pre-set */}
            {user.role === 'superUser' && (
                <NationSelector
                    nations={nations}
                    selectedNation={selectedNation}
                    setSelectedNation={selectNation}
                />
            )}
            {selectedNation && (
                <div className="am-player-filters">
                    <MultiSelectFilter
                        label="Alliance Filter"
                        options={allianceOptions}
                        selected={selectedAllianceIds}
                        setSelected={setAllianceFilter}
                        placeholder="All alliances"
                        pinSelectedToTop
                    />
                    <MultiSelectFilter
                        label="Commander Filter"
                        options={commanderOptions}
                        selected={selectedCommanderIds}
                        setSelected={setSelectedCommanderIds}
                        placeholder="All commanders"
                        pinSelectedToTop
                    />
                    <div className="am-player-filter-summary">
                        <span>
                            {selectedAllianceIds.length > 0 || selectedCommanderIds.length > 0
                                ? `${selectedAllianceIds.length || 'All'} alliance${selectedAllianceIds.length === 1 ? '' : 's'} / ${selectedCommanderIds.length || 'all'} commander${selectedCommanderIds.length === 1 ? '' : 's'}`
                                : 'Showing all alliances and commanders in this nation'}
                        </span>
                        <AppButton
                            kind="secondary"
                            onClick={openStrikeReport}
                            disabled={isStrikeReportLoading || sortedCommanders.length === 0}
                            className="am-filter-strike-report-button"
                        >
                            {isStrikeReportLoading ? 'Building...' : 'Strike Report'}
                        </AppButton>
                    </div>
                </div>
            )}
            {selectedNation && (
                <div className="am-commanders-section">
                    <h3>
                        {selectedAllianceIds.length > 0
                            ? `${sortedCommanders.length.toLocaleString()} of ${commanders.length.toLocaleString()} commanders in selected alliances`
                            : `${sortedCommanders.length.toLocaleString()} of ${commanders.length.toLocaleString()} commanders in nation ${selectedNation.number || selectedNation.name || ''}`}
                    </h3>
                    {isLoading ? (
                        <p>Loading commanders...</p>
                    ) : sortedCommanders.length > 0 ? (
                        <>
                            <div className="am-mobile-sort-controls" aria-label="Player sort controls">
                                <label>
                                    Sort by
                                    <select value={sortConfig.key} onChange={(event) => toggleSort(event.target.value)}>
                                        {PLAYER_TABLE_COLUMNS.map((column) => (
                                            <option key={column.key} value={column.key}>{column.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <button type="button" onClick={() => toggleSort(sortConfig.key)}>
                                    {sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}
                                </button>
                            </div>
                            <table className="am-commanders-table">
                                <thead>
                                    <tr>
                                        {PLAYER_TABLE_COLUMNS.map((column) => (
                                            <th
                                                key={column.key}
                                                aria-sort={
                                                    sortConfig.key === column.key
                                                        ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending')
                                                        : 'none'
                                                }
                                            >
                                                {renderSortableHeader(column)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedCommanders.map((cmd) => (
                                        <tr key={cmd._id}>
                                            <td data-label="Name">{cmd.name || 'N/A'}</td>
                                            <td data-label="Alliance">{cmd.alliance?.acronym || cmd.alliance?.name || 'N/A'}</td>
                                            <td data-label="Rank">{cmd.rank || 'N/A'}</td>
                                            <td data-label="Battle Power">{toNumber(cmd.battlePower).toLocaleString()}</td>
                                            <td data-label="Warning Level">
                                                <div className="am-warning-cell">
                                                    <span className={`am-warning-level warning-${Number(cmd.warningLevel || 0) > 0 ? 'active' : 'none'}`}>
                                                        {Number(cmd.warningLevel || 0).toLocaleString()}
                                                    </span>
                                                    <AppButton
                                                        kind="secondary"
                                                        onClick={() => openStrikeHistory(cmd)}
                                                        className="am-strike-history-button"
                                                    >
                                                        View strike history
                                                    </AppButton>
                                                    {canManageStrikes && (
                                                        <AppButton
                                                            kind="primary"
                                                            onClick={() => openManualStrikeModal(cmd)}
                                                            className="am-strike-history-button"
                                                        >
                                                            Add strike
                                                        </AppButton>
                                                    )}
                                                </div>
                                            </td>
                                            <td data-label="Total Strikes">{toNumber(cmd.totalStrikes).toLocaleString()}</td>
                                            <td data-label="Expected City Location">
                                                <AppButton kind="secondary" onClick={() => copyToClipboard(cmd)} className="am-copy-button">
                                                    {cmd.location || "N/A"}
                                                </AppButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <p>
                            {selectedCommanderIds.length > 0
                                ? 'No commanders found for the selected commander filter.'
                                : selectedAllianceIds.length > 0
                                    ? 'No commanders found for the selected alliance filter.'
                                : 'No commanders found for this nation.'}
                        </p>
                    )}
                </div>
            )}
            {isStrikeReportOpen && (
                <div className="am-strike-modal-backdrop" onClick={() => setIsStrikeReportOpen(false)}>
                    <div className="am-strike-modal am-strike-report-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="am-strike-modal-header">
                            <div>
                                <h3>Strike Report</h3>
                                <span>
                                    Active strikes for the current player filters. Each block is limited to 480 characters.
                                </span>
                            </div>
                            <button type="button" onClick={() => setIsStrikeReportOpen(false)} aria-label="Close strike report">
                                x
                            </button>
                        </div>
                        {isStrikeReportLoading ? (
                            <p>Building strike report...</p>
                        ) : strikeReportError ? (
                            <p className="am-strike-error">{strikeReportError}</p>
                        ) : strikeReportSegments.length > 0 ? (
                            <div className="am-strike-report-blocks">
                                {strikeReportSegments.map((segment, index) => (
                                    <div className="am-strike-report-block" key={`${index}:${segment.length}`}>
                                        <div className="am-strike-report-block-header">
                                            <strong>Block {index + 1}</strong>
                                            <span>{segment.length} / {STRIKE_REPORT_SEGMENT_LIMIT}</span>
                                            <AppButton
                                                kind="secondary"
                                                onClick={() => copyStrikeReportSegment(segment, index)}
                                                className="am-strike-report-copy-button"
                                            >
                                                {copiedStrikeReportIndex === index ? 'Copied' : 'Copy'}
                                            </AppButton>
                                        </div>
                                        <button
                                            type="button"
                                            className="am-strike-report-text"
                                            onClick={() => copyStrikeReportSegment(segment, index)}
                                            aria-label={`Copy strike report block ${index + 1}`}
                                        >
                                            {segment}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p>No active strikes found for the filtered commanders.</p>
                        )}
                    </div>
                </div>
            )}
            {strikeModalCommander && (
                <div className="am-strike-modal-backdrop" onClick={() => { setStrikeModalCommander(null); setEditingStrike(null); }}>
                    <div className="am-strike-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="am-strike-modal-header">
                            <div>
                                <h3>Strike History</h3>
                                <span>
                                    {strikeModalCommander.name}
                                    {' '}
                                    - Warning level {Number(strikeModalCommander.warningLevel || 0).toLocaleString()}
                                </span>
                            </div>
                            <button type="button" onClick={() => { setStrikeModalCommander(null); setEditingStrike(null); }} aria-label="Close strike history">
                                x
                            </button>
                        </div>
                        {isStrikeHistoryLoading ? (
                            <p>Loading strike history...</p>
                        ) : strikeHistoryError ? (
                            <p className="am-strike-error">{strikeHistoryError}</p>
                        ) : strikeHistory.length > 0 ? (
                            <div className="am-strike-table-wrap">
                                <table className="am-strike-table">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            <th>Issued</th>
                                            <th>Expires</th>
                                            <th>Delta</th>
                                            <th>Resulting Level</th>
                                            <th>Reason</th>
                                            <th>Reference</th>
                                            <th>Created By</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {strikeHistory.map((strike) => {
                                            const status = String(strike.effectiveStatus || strike.status || 'active').toLowerCase();
                                            const reason = strike.reason && typeof strike.reason === 'object' ? strike.reason : null;
                                            const hasReportModal = ['EventBattle', 'AoZBattleReport'].includes(String(strike.reasonModel || '')) && reason;
                                            const evidenceRows = Array.isArray(strike.evidence) ? strike.evidence : [];
                                            const reasonTitle = strike.summaryLabel || strike.reasonLabel || strike.reason?.label || 'Manual';
                                            const reasonDetail = strike.summaryDetail || strike.reasonNote || '';
                                            return (
                                                <tr key={strike._id}>
                                                    <td data-label="Status">
                                                        <span className={`am-strike-status status-${status}`}>
                                                            {status}
                                                        </span>
                                                    </td>
                                                    <td data-label="Issued">{formatDateTime(strike.createdAt)}</td>
                                                    <td data-label="Expires">{formatDateTime(strike.expiresAt)}</td>
                                                    <td data-label="Delta">+{Number(strike.warningLevelDelta || 0).toLocaleString()}</td>
                                                    <td data-label="Resulting Level">{Number(strike.resultingWarningLevel || 0).toLocaleString()}</td>
                                                    <td data-label="Reason">
                                                        <div className="am-strike-reference">
                                                            <strong>{reasonTitle}</strong>
                                                            <span>{reasonDetail || strike.reasonNote || 'N/A'}</span>
                                                        </div>
                                                    </td>
                                                    <td data-label="Reference">
                                                        {reason ? (
                                                            <div className="am-strike-reference">
                                                                <strong>{formatReasonType(strike.reasonModel)}</strong>
                                                                <span>{reason.label || reason.reportKey || reason.battleKey || reason.id}</span>
                                                                {evidenceRows.length > 1 && <span>{evidenceRows.length} attached reports</span>}
                                                            </div>
                                                        ) : (
                                                            formatReasonType(strike.reasonModel)
                                                        )}
                                                    </td>
                                                    <td data-label="Created By">{strike.createdBy?.username || 'N/A'}</td>
                                                    <td data-label="Actions">
                                                        <div className="am-strike-actions">
                                                            <AppButton
                                                                kind="secondary"
                                                                onClick={() => setSelectedStrikeReason(reason)}
                                                                disabled={!hasReportModal}
                                                                className="am-strike-report-button"
                                                            >
                                                                View report
                                                            </AppButton>
                                                            <AppButton
                                                                kind="secondary"
                                                                onClick={() => setSelectedEvidenceStrike(strike)}
                                                                disabled={!evidenceRows.length}
                                                                className="am-strike-report-button"
                                                            >
                                                                View evidence
                                                            </AppButton>
                                                            {canEditStrikes && (
                                                                <AppButton
                                                                    kind="secondary"
                                                                    onClick={() => openEditStrikeModal(strike)}
                                                                    className="am-strike-report-button"
                                                                >
                                                                    Edit
                                                                </AppButton>
                                                            )}
                                                            {canManageStrikes && (
                                                                <AppButton
                                                                    kind="danger"
                                                                    onClick={() => deleteStrike(strike)}
                                                                    disabled={isDeletingStrikeId === strike._id}
                                                                    className="am-strike-delete-button"
                                                                >
                                                                    {isDeletingStrikeId === strike._id ? 'Deleting...' : 'Delete'}
                                                                </AppButton>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p>No strikes have been recorded for this commander.</p>
                        )}
                    </div>
                </div>
            )}
            {editingStrike && (
                <div className="am-strike-modal-backdrop" onClick={() => setEditingStrike(null)}>
                    <div className="am-strike-modal am-manual-strike-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="am-strike-modal-header">
                            <div>
                                <h3>Edit Strike</h3>
                                <span>
                                    {strikeModalCommander?.name || editingStrike.commander?.name || 'Commander'}
                                    {' '}
                                    - {String(editingStrike.effectiveStatus || editingStrike.status || 'active').toLowerCase()}
                                </span>
                            </div>
                            <button type="button" onClick={() => setEditingStrike(null)} aria-label="Close edit strike">
                                x
                            </button>
                        </div>
                        <div className="am-manual-strike-form">
                            <label>
                                Expiry
                                <input
                                    type="datetime-local"
                                    value={editStrikeForm.expiresAt}
                                    onChange={(event) => setEditStrikeForm((prev) => ({ ...prev, expiresAt: event.target.value }))}
                                />
                            </label>
                            <label className="am-manual-strike-note">
                                Reason note
                                <textarea
                                    rows={5}
                                    value={editStrikeForm.reasonNote}
                                    onChange={(event) => setEditStrikeForm((prev) => ({ ...prev, reasonNote: event.target.value }))}
                                    placeholder="Why this commander received this strike"
                                />
                            </label>
                            <div className="am-strike-evidence-editor">
                                <div className="am-strike-evidence-editor-header">
                                    <strong>Attached evidence</strong>
                                    <span>{editEvidenceRefs.length.toLocaleString()} report(s)</span>
                                </div>
                                {editEvidenceRefs.length > 0 ? (
                                    <div className="am-strike-evidence-list">
                                        {editEvidenceRefs.map((evidence, index) => (
                                            <div className="am-strike-evidence-row" key={`${evidence.reasonModel}:${evidence.reasonRef}:${index}`}>
                                                <div>
                                                    <strong>{evidence.label || formatReasonType(evidence.reasonModel)}</strong>
                                                    <span>{evidence.summary || evidence.reasonRef}</span>
                                                </div>
                                                <AppButton kind="danger" onClick={() => removeEditEvidenceRef(index)}>
                                                    Remove
                                                </AppButton>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="am-strike-evidence-empty">No evidence reports are attached.</p>
                                )}
                                <div className="am-strike-evidence-add">
                                    <label>
                                        Type
                                        <select
                                            value={newEvidenceForm.reasonModel}
                                            onChange={(event) => setNewEvidenceForm((prev) => ({ ...prev, reasonModel: event.target.value }))}
                                        >
                                            <option value="EventBattle">Battle log row</option>
                                            <option value="AoZBattleReport">Battle report</option>
                                            <option value="EventResultEntry">Event result</option>
                                        </select>
                                    </label>
                                    <label>
                                        Report or battle id
                                        <input
                                            value={newEvidenceForm.reasonRef}
                                            onChange={(event) => setNewEvidenceForm((prev) => ({ ...prev, reasonRef: event.target.value }))}
                                            placeholder="Mongo document id"
                                        />
                                    </label>
                                    <label>
                                        Label
                                        <input
                                            value={newEvidenceForm.label}
                                            onChange={(event) => setNewEvidenceForm((prev) => ({ ...prev, label: event.target.value }))}
                                            placeholder="Optional display label"
                                        />
                                    </label>
                                    <label className="am-manual-strike-note">
                                        Summary
                                        <input
                                            value={newEvidenceForm.summary}
                                            onChange={(event) => setNewEvidenceForm((prev) => ({ ...prev, summary: event.target.value }))}
                                            placeholder="Optional one-line summary"
                                        />
                                    </label>
                                    <div className="am-strike-evidence-add-actions">
                                        <AppButton kind="secondary" onClick={addEditEvidenceRef}>
                                            Add Evidence
                                        </AppButton>
                                    </div>
                                </div>
                            </div>
                            <div className="am-manual-strike-actions">
                                <AppButton
                                    kind="primary"
                                    onClick={() => submitEditStrike()}
                                    disabled={isSavingEditStrike || isExpiringStrike}
                                >
                                    {isSavingEditStrike ? 'Saving...' : 'Save Strike'}
                                </AppButton>
                                <AppButton
                                    kind="danger"
                                    onClick={() => submitEditStrike({ expireImmediately: true })}
                                    disabled={isSavingEditStrike || isExpiringStrike}
                                >
                                    {isExpiringStrike ? 'Expiring...' : 'Expire Now'}
                                </AppButton>
                                <AppButton
                                    kind="secondary"
                                    onClick={() => setEditingStrike(null)}
                                    disabled={isSavingEditStrike || isExpiringStrike}
                                >
                                    Cancel
                                </AppButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {selectedEvidenceStrike && (
                <div className="am-strike-modal-backdrop" onClick={() => setSelectedEvidenceStrike(null)}>
                    <div className="am-strike-modal am-strike-evidence-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="am-strike-modal-header">
                            <div>
                                <h3>Strike Evidence</h3>
                                <span>
                                    {selectedEvidenceStrike.summaryLabel || selectedEvidenceStrike.reasonLabel || 'Strike'}
                                    {selectedEvidenceStrike.summaryDetail ? ` - ${selectedEvidenceStrike.summaryDetail}` : ''}
                                </span>
                            </div>
                            <button type="button" onClick={() => setSelectedEvidenceStrike(null)} aria-label="Close strike evidence">
                                x
                            </button>
                        </div>
                        <div className="am-strike-evidence-list">
                            {(Array.isArray(selectedEvidenceStrike.evidence) ? selectedEvidenceStrike.evidence : []).map((evidence, index) => {
                                const hasReportModal = ['EventBattle', 'AoZBattleReport'].includes(String(evidence.reasonModel || '')) && evidence.reason;
                                return (
                                    <div className="am-strike-evidence-row" key={`${evidence.reasonModel}:${evidence.reasonRef}:${index}`}>
                                        <div>
                                            <strong>{evidence.label || `${formatReasonType(evidence.reasonModel)} ${index + 1}`}</strong>
                                            <span>{evidence.summary || evidence.reason?.label || evidence.reasonRef}</span>
                                            <small>{formatDateTime(evidence.battleTime)}</small>
                                        </div>
                                        <AppButton
                                            kind="secondary"
                                            onClick={() => setSelectedStrikeReason(evidence.reason)}
                                            disabled={!hasReportModal}
                                        >
                                            View report
                                        </AppButton>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            {manualStrikeCommander && (
                <div className="am-strike-modal-backdrop" onClick={() => setManualStrikeCommander(null)}>
                    <div className="am-strike-modal am-manual-strike-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="am-strike-modal-header">
                            <div>
                                <h3>Add Strike</h3>
                                <span>
                                    {manualStrikeCommander.name}
                                    {' '}
                                    - Current warning level {Number(manualStrikeCommander.warningLevel || 0).toLocaleString()}
                                </span>
                            </div>
                            <button type="button" onClick={() => setManualStrikeCommander(null)} aria-label="Close add strike">
                                x
                            </button>
                        </div>
                        <div className="am-manual-strike-form">
                            <label>
                                Warning level delta
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={manualStrikeForm.warningLevelDelta}
                                    onChange={(event) => setManualStrikeForm((prev) => ({ ...prev, warningLevelDelta: event.target.value }))}
                                />
                            </label>
                            <label>
                                Expiry days
                                <input
                                    type="number"
                                    min="1"
                                    max="3650"
                                    value={manualStrikeForm.expiryDays}
                                    onChange={(event) => setManualStrikeForm((prev) => ({ ...prev, expiryDays: event.target.value }))}
                                />
                            </label>
                            <label className="am-manual-strike-note">
                                Reason note
                                <textarea
                                    rows={5}
                                    value={manualStrikeForm.reasonNote}
                                    onChange={(event) => setManualStrikeForm((prev) => ({ ...prev, reasonNote: event.target.value }))}
                                    placeholder="Why this commander is receiving a manual strike"
                                />
                            </label>
                            <div className="am-manual-strike-actions">
                                <AppButton kind="primary" onClick={submitManualStrike} disabled={isSavingManualStrike}>
                                    {isSavingManualStrike ? 'Adding...' : 'Add Strike'}
                                </AppButton>
                                <AppButton kind="secondary" onClick={() => setManualStrikeCommander(null)} disabled={isSavingManualStrike}>
                                    Cancel
                                </AppButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {selectedStrikeReason && (
                <BattleReportModal
                    reason={selectedStrikeReason}
                    title="Strike Report"
                    onClose={() => setSelectedStrikeReason(null)}
                />
            )}
        </div>
    );
};

export default PlayerManagementTab;
